// ===========================================
// SERVICE: Responsables por cliente (CORE) — ORR-230 fase 4
// ===========================================
//
// El mapa `cliente_id → usuario_id[]` que la planilla necesita para scopear por responsable
// sin parsear nombres. Las vistas de vencimientos solo emiten `cliente_responsable`, un
// `string_agg(u.nombre, ', ')`: con eso, un apellido con coma inventa un responsable que no
// existe y dos personas homónimas son indistinguibles. El id no tiene esos problemas y además
// es lo que necesita el reparto de conceptos, que se asigna por `usuario_id`.

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { fetchAllPages } from '@/lib/postgrest-chunks'

/** Los responsables de cada cliente. La ausencia de clave significa "cliente sin responsables". */
export type MapaResponsables = Map<string, Set<string>>

interface FilaResponsable {
  cliente_id: string
  usuario_id: string
}

export const responsablesService = {
  /**
   * Mapa de responsables de toda la organización.
   *
   * `cliente_responsables` no tiene `organizacion_id`, así que el scoping multi-tenant sale del
   * join con `clientes` — RLS no filtra por organización en BrainHub, la app sí.
   */
  async listarMapa(
    client: SupabaseClient<Database>,
    organizacionId: string,
  ): Promise<MapaResponsables> {
    const filas = await fetchAllPages<FilaResponsable>(async (desde, hasta) => {
      const { data, error } = await client
        .from('cliente_responsables')
        // Mismo hint por columna que `impuestosPageService`, que ya corre en PROD.
        .select('cliente_id, usuario_id, cliente:clientes!cliente_id!inner(organizacion_id)')
        .eq('cliente.organizacion_id', organizacionId)
        // Orden estable por PK: sin él, dos páginas del mismo `.range()` pueden repetir o
        // saltear filas, y el mapa quedaría incompleto sin ningún error visible.
        .order('id')
        .range(desde, hasta)

      if (error) throw new Error(`Error al listar responsables por cliente: ${error.message}`)
      return (data ?? []) as unknown as FilaResponsable[]
    })

    const mapa: MapaResponsables = new Map()

    for (const fila of filas) {
      const actuales = mapa.get(fila.cliente_id)
      if (actuales) actuales.add(fila.usuario_id)
      else mapa.set(fila.cliente_id, new Set([fila.usuario_id]))
    }

    return mapa
  },
}
